/**
 * "Activities by watershed" — the Overview map's own floating widget.
 *
 * **What the client asked for (8 Sep 2026, R1 items 5 and 7).** The widget was pinned to the
 * top-left of the map, under the filter bar, as a gradient key and nothing else:
 *
 * * *"Default location should be bottom left. And allow user to freely move it around."* —
 *   so it anchors bottom-left, above the ticker and clear of Leaflet's attribution, and it is
 *   dragged by its own header through `lib/draggable`, the pattern every §F5 map widget
 *   already uses. Position survives the session in `sessionStorage`, which is the same
 *   lifetime as the splash's own "seen it once" flag.
 * * *"No need showing numbers on the watershed."* — the floating counts came off the
 *   polygons, so **this widget is where the counts now live**. A ramp with a min and a max
 *   answers "which is darker"; it does not answer "how many", and that number had to keep a
 *   home. Hence the ranked list: every basin in scope, busiest first, with its count.
 *
 * The list is the map's own rows, so it follows the filter exactly as the polygons do, and
 * hovering a row is the same tooltip sentence the polygon carries.
 */
import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { clampPosition, useDraggable, type Position } from '../../lib/draggable';
import { fmtNum } from '../../lib/format';
import { COUNTRY_NAMES, type Country } from '../../lib/state';
import type { WatershedRow, WatershedShading } from './activityPoints';
import { WATERSHED_NODATA_FILL } from './activityPoints';
import { readStoredPosition, WIDGET_POSITION_KEY, type ActivityExtremes } from './overview';

export interface WatershedWidgetProps {
  /** The map slot — the box the widget may never be dragged out of. */
  bounds: RefObject<HTMLElement | null>;
  /** The basins currently drawn, already scoped by the filter, busiest first. */
  rows: readonly WatershedRow[];
  shading: WatershedShading;
  extremes: ActivityExtremes;
  /** True while partnership boundaries are on the map — the key names only what is drawn. */
  showPartnerships: boolean;
  partnershipColor: string;
  /** Activities that belong to no basin at all. */
  unassigned: number;
  /** Clicking a row selects that basin, exactly as clicking its polygon does. */
  onPick: (unitId: string) => void;
}

export function WatershedWidget({
  bounds,
  rows,
  shading,
  extremes,
  showPartnerships,
  partnershipColor,
  unassigned,
  onPick,
}: WatershedWidgetProps) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);

  useDraggable(boxRef, handleRef, { bounds });

  /** Write the widget's current corner down, in container coordinates. */
  const persist = useCallback(() => {
    const el = boxRef.current;
    const host = bounds.current;
    // No inline `left` means the widget is still on its CSS anchor and has never been moved:
    // storing a position for it would pin it where it merely happens to be, and the anchor is
    // what keeps it clear of the ticker when the card is resized.
    if (!el || !host || !el.style.left) return;
    const hr = host.getBoundingClientRect();
    const wr = el.getBoundingClientRect();
    try {
      window.sessionStorage.setItem(
        WIDGET_POSITION_KEY,
        JSON.stringify({ left: Math.round(wr.left - hr.left), top: Math.round(wr.top - hr.top) }),
      );
    } catch {
      /* a private window with storage denied still gets a draggable widget */
    }
  }, [bounds]);

  // Restore, once the element and its host both have a size. `clampPosition` is the drag's own
  // maths, so a position stored on a wider card cannot put the widget off a narrower one.
  useEffect(() => {
    const el = boxRef.current;
    const host = bounds.current;
    if (!el || !host) return;
    let stored: Position | null = null;
    try {
      stored = readStoredPosition(window.sessionStorage.getItem(WIDGET_POSITION_KEY));
    } catch {
      stored = null;
    }
    if (!stored) return;
    const hr = host.getBoundingClientRect();
    if (hr.width === 0 || hr.height === 0) return;
    const p = clampPosition(
      { ...stored, width: el.offsetWidth, height: el.offsetHeight },
      { width: hr.width, height: hr.height },
    );
    el.style.left = `${p.left}px`;
    el.style.top = `${p.top}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  }, [bounds]);

  // The gesture ends on the window (see `lib/draggable`), so that is where the write hangs.
  // A resize re-clamps the widget without any pointer at all, and is saved for the same reason.
  useEffect(() => {
    window.addEventListener('pointerup', persist);
    window.addEventListener('resize', persist);
    return () => {
      window.removeEventListener('pointerup', persist);
      window.removeEventListener('resize', persist);
    };
  }, [persist]);

  const listed = useMemo(() => rows.filter((row) => row.name), [rows]);

  return (
    <div
      id="hpMapKey"
      ref={boxRef}
      className="glass ov-widget"
      data-testid="overview-map-key"
      data-widget="watersheds"
    >
      <div className="ov-widget-head" ref={handleRef} data-testid="overview-widget-handle">
        <b>{t('splash.map.keyTitle')}</b>
        <span className="ov-widget-grip" aria-hidden="true" />
      </div>
      <div className="hp-key-strip" aria-hidden="true">
        {shading.colors.map((color, i) => (
          <i key={i} style={{ background: color }} />
        ))}
      </div>
      <div className="hp-key-scale">
        <span>{shading.hasData ? fmtNum(shading.min, 0) : '—'}</span>
        <span>{shading.hasData ? fmtNum(shading.max, 0) : '—'}</span>
      </div>

      {/* R1 item 7: the counts came off the polygons, so they are written here instead. */}
      {listed.length > 0 && (
        <ul className="ov-ws-list" data-testid="overview-watershed-counts">
          {listed.map((row) => (
            <li key={row.unit_id}>
              <button
                type="button"
                data-ws-row={row.unit_id}
                data-count={row.count}
                onClick={() => onPick(row.unit_id)}
                title={[
                  row.name,
                  row.country
                    ? t(`country.${row.country}`, COUNTRY_NAMES[row.country as Country] ?? row.country)
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <span className="ov-ws-name">{row.name}</span>
                {extremes.most === row.unit_id && (
                  <i className="ov-ws-flag ov-ws-most" title={t('overview.map.mostActive')} />
                )}
                {extremes.least === row.unit_id && (
                  <i className="ov-ws-flag ov-ws-least" title={t('overview.map.leastActive')} />
                )}
                <span className="ov-ws-n">{fmtNum(row.count, 0)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {listed.some((row) => row.count === 0) && (
        <div className="hp-key-note">
          <i style={{ background: WATERSHED_NODATA_FILL }} />
          {t('splash.map.keyNone')}
        </div>
      )}
      {/* The partnership layer names itself only while it is on the map, so the key is a
          description of what is drawn rather than a list of what could be. */}
      {showPartnerships && (
        <div className="hp-key-note" data-testid="overview-key-partnerships">
          <i style={{ background: partnershipColor, borderColor: partnershipColor }} />
          {t('overview.map.partnerships')}
        </div>
      )}
      {unassigned > 0 && (
        <div className="hp-key-note" data-testid="splash-unassigned">
          {t('splash.map.unassigned', { count: unassigned, value: fmtNum(unassigned, 0) })}
        </div>
      )}
    </div>
  );
}

export default WatershedWidget;
